import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, UserCog, KeyRound, ShieldOff, Bell } from "lucide-react";

const EMPTY = { nome: "", email: "", email_notifiche: "", password: "", note: "" };

export default function Collaborators() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/collaborators").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ nome: c.nome, email: c.email || "", email_notifiche: c.email_notifiche || "", password: "", note: c.note || "" }); setEditId(c.id); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.nome.trim() || !form.email.trim()) return toast.error("Nome ed email sono obbligatori");
    if (!editId && !form.password) return toast.error("Imposta una password per l'accesso");
    try {
      const payload = { nome: form.nome, email: form.email, email_notifiche: form.email_notifiche, note: form.note };
      if (form.password) payload.password = form.password;
      if (editId) await api.put(`/collaborators/${editId}`, payload);
      else await api.post("/collaborators", payload);
      toast.success(editId ? "Collaboratore aggiornato" : "Collaboratore creato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo collaboratore? Perderà l'accesso al gestionale.")) return;
    await api.delete(`/collaborators/${id}`); toast.success("Collaboratore eliminato"); load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><UserCog className="h-7 w-7 text-dusty-600" /> Collaboratori</h1>
          <p className="text-sm text-slate-500 mt-1">Accesso allo staff operativo senza visibilità sull'area finanziaria.</p>
        </div>
        <Button onClick={openNew} data-testid="add-collaborator-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo Collaboratore
        </Button>
      </div>

      <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-4 flex items-start gap-3" data-testid="collab-info">
        <div className="h-9 w-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0"><ShieldOff className="h-5 w-5" /></div>
        <div className="text-sm text-indigo-900/80">
          I collaboratori possono gestire aziende, corsi, calendario, ordini, attestati, vendite, partner e fondi.
          <b> Non vedono</b> Import Sibill, Stato Pagamenti, i progetti finanziati e i dati finanziari della dashboard.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <Card key={c.id} className="p-5 border-slate-200 shadow-sm" data-testid={`collaborator-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <UserCog className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-800">{c.nome}</div>
                  {c.email && <div className="text-xs text-slate-500">{c.email}</div>}
                  {c.email_notifiche && <div className="text-xs text-dusty-600 flex items-center gap-1"><Bell className="h-3 w-3" />{c.email_notifiche}</div>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-collaborator-${c.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(c.id)} data-testid={`delete-collaborator-${c.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-emerald-600"><KeyRound className="h-3 w-3" /> Accesso attivo</div>
            {c.note && <div className="text-sm text-slate-500 mt-2">{c.note}</div>}
          </Card>
        ))}
        {items.length === 0 && <div className="text-slate-400 col-span-full py-12 text-center">Nessun collaboratore. Aggiungine uno.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="collaborator-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Modifica Collaboratore" : "Nuovo Collaboratore"}</DialogTitle>
            <DialogDescription>Imposta email e password per dare al collaboratore l'accesso operativo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome *</Label><Input value={form.nome} onChange={set("nome")} data-testid="collaborator-nome" className="mt-1.5" /></div>
            <div><Label>Email (accesso) *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="collaborator-email" className="mt-1.5" /></div>
            <div><Label>Email notifiche (facoltativa)</Label><Input type="email" value={form.email_notifiche} onChange={set("email_notifiche")} placeholder="Lascia vuoto per usare l'email di accesso" data-testid="collaborator-email-notifiche" className="mt-1.5" /></div>
            <div><Label>Password {editId && "(lascia vuoto per non cambiare)"}</Label><Input type="password" value={form.password} onChange={set("password")} data-testid="collaborator-password" className="mt-1.5" /></div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={set("note")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="collaborator-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
